import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Dropdown,
  InputNumber,
  Menu,
  Pagination,
  Table,
  Tabs,
  Skeleton,
  message,
} from "antd";

import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router";
import AssignUnitGroup from "../../components/DrawerLayout/components/AssignUnitGroup";
import { EditIcon, EyeIcon, InactiveIcon, MergedTicketIcon } from "../../icons";
import "./style.scss";
import { ActivateIcon, DeactivateIcon } from "../../layout/Components/icons";
import ConfirmModal from "../../components/ModalLayout/components/ConfirmModal";
import {
  useActivateDeactivateUnitGroup,
  useDeleteUnitGroup,
  useUnitGroups,
} from "../../api/unitGroups";
import FilterUnitGroup from "../../components/FilterUnitGroup";
import { isPermit } from "../../utils/permissionCheck";
import PermissionDenied from "../OtherPage/PermissionDenied";

interface Unit {
  id: number;
  srNo: string;
  name: string;
  callType: string[];
  status: string;
  members: string[];
  createdOn: string;
  description: string;
  unitFlag?: "rejected" | "merged" | "primary" | null;
}

interface UnitTableProps {
  isMobile?: boolean;
  dataSource?: Unit[];
  scroll?: { x?: number; y?: number };
  pagination?: boolean;
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const statusClass = status.toLowerCase().replace(" ", "-");
  return <span className={`status-pill status-${statusClass}`}>{status}</span>;
};
const UnitTable: React.FC<UnitTableProps> = () => {
  const [selectedRowKeys] = useState<React.Key[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState<{
    title: string;
    message?: string;
    onConfirm: () => void;
    confirmText?: string;
  } | null>(null);

  const navigate = useNavigate();
  const deleteMutation = useDeleteUnitGroup();
  const activateDeactivateMutation = useActivateDeactivateUnitGroup();

  const handleDelete = async (unitId: number) => {
    try {
      await deleteMutation.mutateAsync({
        action: "delete",
        unit_group_id: unitId,
      });
      message.success("Unit group deleted successfully");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to delete unit group"
      );
    }
  };
  const handleActivate = async (unitId: number) => {
    try {
      await activateDeactivateMutation.mutateAsync({
        action: "activate",
        unit_group_id: unitId,
      });
      message.success("Unit group activated successfully!");
    } catch (error: any) {
      console.error("Activate Error:", error);
      message.error(
        error.response?.data?.message ||
          "Failed to activate the unit group. Please try again."
      );
    }
  };

  const handleDeactivate = async (unitId: number) => {
    try {
      await activateDeactivateMutation.mutateAsync({
        action: "deactivate",
        unit_group_id: unitId,
      });
      message.success("Unit group deactivated successfully!");
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          "Failed to deactivate the unit group. Please try again."
      );
    }
  };
  const ActionMenu = (record: Unit) => {
    const isActive = record.status === "Active";

    return (
      <Menu
        onClick={({ key }) => {
          // In UnitTable.tsx
          if (key === "view") {
            navigate(`/unit/d/${record.srNo}`, { state: { unit: record } });
          }

          if (key === "edit") {
            setDrawerMode("edit");
            setDrawerOpen(true);
            setSelectedUnit(record);
          }

          if (key === "status") {
            setModalProps({
              title: isActive ? "Confirm Deactivate" : "Confirm Activate",
              message: `Are you sure you want to ${
                isActive ? "deactivate" : "activate"
              } ${record.name}?`,
              onConfirm: () =>
                isActive
                  ? handleDeactivate(record.id)
                  : handleActivate(record.id),
              confirmText: isActive ? "Deactivate" : "Activate",
            });
            setModalOpen(true);
          }

          if (key === "delete") {
            setModalProps({
              title: "Confirm Delete",
              message: `Are you sure you want to delete ${record.name}? This action cannot be undone.`,
              onConfirm: () => handleDelete(record.id),
              confirmText: "Delete",
            });
            setModalOpen(true);
          }
        }}
        items={[
          ...(isPermit(4, "edit")
            ? [{ icon: <EditIcon />, key: "edit", label: "Edit Unit" }]
            : []),

          { icon: <EyeIcon />, key: "view", label: "View Unit" },

          ...(isPermit(4, "delete")
            ? [
                {
                  icon: <InactiveIcon />,
                  key: "delete",
                  danger: true,
                  label: "Remove Unit",
                },
              ]
            : []),
          ...(isPermit(4, "edit")
            ? [
                {
                  icon: isActive ? (
                    <span>
                      <DeactivateIcon />
                    </span>
                  ) : (
                    <span>
                      <ActivateIcon />
                    </span>
                  ),
                  key: "status",
                  label: isActive ? "Deactivate" : "Activate unit",
                },
              ]
            : []),
        ]}
      />
    );
  };

  const columns: ColumnsType<Unit> = [
    {
      title: "SR no",
      dataIndex: "srNo",
      key: "srNo",
      width: 150,
      fixed: "left",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (name: string, record: Unit) => (
        <span
          className="name-link"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/unit/d/${record.srNo}`)}
        >
          {name}
        </span>
      ),
    },
    {
      title: "Call type",
      dataIndex: "callType",
      key: "callType",
      width: 250,
      render: (callType: string[]) => {
        if (!callType || callType.length === 0) return "-";
        const [first, ...rest] = callType;
        return (
          <span>
            {first}
            {rest.length > 0 && (
              <span className="count">
                {" "}
                {rest.length > 0 && ` +${rest.length}`}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status: string) => <StatusPill status={status} />,
    },
    {
      title: "Members",
      dataIndex: "members",
      key: "members",
      width: 250,
      render: (members: string[]) => {
        if (!members || members.length === 0) return "-";
        const [first, ...rest] = members;
        return (
          <span>
            {first}
            {rest.length > 0 && (
              <span className="count">
                {" "}
                {rest.length > 0 && ` +${rest.length}`}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: "Depot name",
      dataIndex: "depotName",
      key: "depotName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Created on",
      dataIndex: "createdOn",
      key: "createdOn",
      width: 200,
    },
    {
      title: "",
      key: "actions",
      width: 50,
      fixed: "right",
      render: (_text, record: Unit) => (
        <Dropdown overlay={ActionMenu(record)} trigger={["click"]}>
          <Button icon={<MoreOutlined />} type="text" />
        </Dropdown>
      ),
    },
  ];

  const skeletonColumns = useMemo(() => {
    return columns.map((col) => {
      if (col.key !== "actions") {
        return {
          ...col,
          render: () => (
            <Skeleton.Input
              active
              size="small"
              style={{
                width: "100%",
                height: "20px",
              }}
            />
          ),
        };
      }
      return col;
    });
  }, [columns]);

  const tableHeaders = columns
    .filter((col) => col.key && col.key !== "actions")
    .map((col) => {
      if ("dataIndex" in col) {
        return {
          label: col.title as string,
          value: col.dataIndex as string,
        };
      }
      return null;
    })
    .filter(Boolean) as { label: string; value: string }[];
  const [modalLoading, setModalLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [, setMergedDrawerOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | undefined>("all");
  const [filterData, setFilterData] = useState<{
    status?: number;
    call_types_in?: string[];
    created_last_week?: boolean;
  }>({});
  const [allTabTotal, setAllTabTotal] = useState<number | null>(null);
  const [totalPageCount, setTotalPageCount] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  // const tableData = dataSource ?? unitData;
  const tableHeight = window.innerHeight - 380;

  const { data, isLoading, refetch } = useUnitGroups(
    {
      ...filterData,

      status:
        selectedType === "all" ? undefined : selectedType === "Active" ? 1 : 0,
    },
    true,
    currentPage,
    pageSize
  );

  //  const { data, isLoading, isError } = useUnitGroups(currentPage, pageSize);

  const totalUnits = data?.data?.meta?.total ?? 0;

  const tableData =
    data?.data?.unitGroups?.map((group: any) => ({
      id: group.pikitup_unit_group_id,
      srNo: `${group.reference_no}`,
      name: group.unit_group_name,
      callType: group.call_types.map((c: any) => c.service_long_description),
      status: group.status_flg === 1 ? "Active" : "Inactive",
      membersId: group.members.map((m: any) => m.pikitup_member_id),
      members: group.members.map((m: any) => m.member_first_name),
      membersRefNo: group.members.map((m: any) => m.reference_no),
      membersType: group.members.map((m: any) => m.member_type_name),
      createdOn: group.created_date,
      depotName: group.depot_name,
      description: group.call_types
        .map((c: any) => c.service_long_description)
        .join(", "),
      unit_group_desc: group.unit_group_desc || "",
      record: group,
    })) ?? [];

  const skeletonData = useMemo(
    () =>
      Array.from({ length: pageSize }).map((_, index) => ({
        id: `skeleton-${index}-${Date.now()}`,
        srNo: "",
        name: "",
        callType: [],
        status: "",
        members: [],
        createdOn: "",
        description: "",
      })),
    [pageSize]
  );

  const displayData = isLoading ? skeletonData : tableData;
  const displayColumns = isLoading ? skeletonColumns : columns;

  const filteredData = displayData.filter((unit) => {
    if (selectedType === "all") return true;
    return unit.status === selectedType;
  });

  const items: any = [
    {
      key: "all",
      label: `All${allTabTotal !== null ? ` (${allTabTotal})` : ""}`,
    },
    { key: "Active", label: "Active" },
    { key: "Inactive", label: "Inactive" },
  ];

  const optionsByColumn: Record<string, string[]> = {
    srNo: tableData.map((t) => t.srNo),
    name: [...new Set(tableData.map((t) => t.name))],
    callType: [...new Set(tableData.flatMap((t) => t.callType))],
    status: [...new Set(tableData.map((t) => t.status))],
    members: [...new Set(tableData.flatMap((t) => t.members))],
    createdOn: [...new Set(tableData.map((t) => t.createdOn))],
  };

  useEffect(() => {
    if (
      currentPage === 1 &&
      allTabTotal === null &&
      data?.data?.meta?.total != null
    ) {
      setAllTabTotal(data.data.meta.total);
      setTotalPageCount(Math.ceil(data.data.meta.total / pageSize));
    } else if (currentPage === 1) {
      setTotalPageCount(Math.ceil(totalUnits));
    }
  }, [currentPage, allTabTotal, data]);

  return (
    <>
      {isPermit(4, "view") ? (
        <div className="unit-dashboard">
          <div className="unit-header">
            <div className="flex items-center gap-4">
              <h2 className="unit-title">Unit groups</h2>

              <ConfigProvider
                theme={{
                  components: {
                    Tabs: {
                      inkBarColor: "#1677ff",
                      itemSelectedColor: "#1677ff",
                      itemHoverColor: "#4096ff",
                      itemColor: "rgba(0,0,0,0.88)",
                    },
                  },
                }}
              >
                <Tabs
                  defaultActiveKey="all"
                  activeKey={selectedType}
                  onChange={(key) => {
                    setCurrentPage(1);
                    setSelectedType(key);
                  }}
                  items={items}
                  className="status-tabs"
                />
              </ConfigProvider>
            </div>
            {isPermit(4, "create") && (
              <button
                onClick={() => {
                  setDrawerOpen(true), setDrawerMode("add");
                }}
                className="add-unit-btn"
              >
                ADD UNIT GROUP
              </button>
            )}
          </div>
          <div className="table-controls">
            <div className="table-filter-wrapper flex gap-3">
              {selectedRowKeys.length >= 2 && isPermit(2, "merge") && (
                <div className="merge-btn-wrapper">
                  <Button
                    onClick={() => setMergedDrawerOpen(true)}
                    icon={<MergedTicketIcon />}
                    className="transparent merge-ticket-btn"
                  >
                    {" "}
                    MERGE REQUEST
                  </Button>
                </div>
              )}
              <div className="left-controls">
                <FilterUnitGroup
                  activeTab={selectedType}
                  tableHeaders={
                    selectedType === "all"
                      ? tableHeaders
                      : tableHeaders.filter((col) => col.label !== "Status")
                  }
                  optionsByColumn={optionsByColumn}
                  tableData={tableData}
                  onChange={({ apiParams }) => {
                    setFilterData(apiParams);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="table-wrapper">
            <Table
              loading={isLoading}
              rowKey="id"
              columns={displayColumns}
              dataSource={filteredData}
              // rowSelection={rowSelection}
              scroll={{ x: 1000, y: tableHeight }}
              sticky={{ offsetHeader: 0 }}
              pagination={false}
            />
          </div>

          <div className="pagination-wrapper">
            <div className="pagination-info">
              Page
              <InputNumber
                controls={false}
                min={1}
                max={totalPageCount ? Math.ceil(totalPageCount / pageSize) : 1}
                value={currentPage}
                onChange={(val) => setCurrentPage(val || 1)}
                style={{ width: 60 }}
              />
              <span>
                of
                {totalPageCount ? Math.ceil(totalPageCount / pageSize) : 1}
              </span>
            </div>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalPageCount ? totalPageCount : 0}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        </div>
      ) : (
        <PermissionDenied />
      )}
      <AssignUnitGroup
        open={drawerOpen}
        setOpen={setDrawerOpen}
        title={drawerMode === "edit" ? "Edit unit group" : "Add unit group"}
        unit={selectedUnit as any}
        mode={drawerMode}
        refetchUnit={refetch}
      />
      {modalOpen && (
        <ConfirmModal
          loading={modalLoading}
          open={modalOpen}
          title={modalProps?.title ?? ""}
          message={modalProps?.message}
          confirmText={modalProps?.confirmText}
          onClose={() => {
            if (!modalLoading) {
              setModalOpen(false);
              setModalProps(null);
            }
          }}
          onConfirm={async () => {
            if (!modalProps?.onConfirm) return;

            setModalLoading(true);
            try {
              await modalProps.onConfirm();
              setModalOpen(false);
              setModalProps(null);
            } catch (error) {
              message.error("Action failed. Please try again.");
            } finally {
              setModalLoading(false);
            }
          }}
        />
      )}
    </>
  );
};

export default UnitTable;
